import * as React from 'react';
import { FC } from 'react';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { AutocompleteTextField, CheckInputField, NumberInputField } from '../../../components/Forms/fields';
import { ServiceSettings } from '../../../models/api-models';
import InvoiceItemDetailsForm from '../../../components/Forms/InvoiceItemDetailsForm/InvoiceItemDetailsForm';
import { InfoBox } from '../../../components/UI/InfoBox';

const MAX_SEND_INVOICE_DAYS_BEFORE_APPOINTMENT = 90;

interface ServiceConfigurationInvoicesTabProps {
    id: string;
    data: ServiceSettings;
    paymentMethods: string[];
    setData: React.Dispatch<React.SetStateAction<ServiceSettings>>;
}

const ServiceConfigurationInvoicesTab: FC<ServiceConfigurationInvoicesTabProps> = ({
    id,
    data,
    paymentMethods,
    setData,
}) => {
    const t = useTranslations();

    return (
        <>
            <InfoBox>
                <small>{t(TK.invoiceDataDisclaimer)}</small>
            </InfoBox>
            <InvoiceItemDetailsForm
                id={`${id}-invoice-item-details`}
                value={data.invoiceItemDetails}
                onChange={invoiceItemDetails => setData(d => ({ ...d, invoiceItemDetails: invoiceItemDetails ?? undefined }))}
                parentContext="service"
                parentName={data.name}
            />
            <div style={{
                marginTop: 'var(--spacer-S)',
                marginBottom: 'var(--spacer-S)',
                padding: 'var(--spacer-S)',
                border: 'var(--border-thin) solid var(--appLigthGrey)',
                borderRadius: 8,
                backgroundColor: 'var(--bg-light)',
            }}>
                <CheckInputField
                    id={`${id}-send-receipt-before-appointment`}
                    label={t(TK.sendInvoiceBeforeAppointment)}
                    helpText={t(TK.sendInvoiceBeforeAppointmentTooltip)}
                    value={data.sendInvoiceBeforeAppointment ?? false}
                    onChange={value => setData(d => ({
                        ...d,
                        sendInvoiceBeforeAppointment: value,
                        sendInvoiceDaysBeforeAppointment: value
                            ? Math.min(MAX_SEND_INVOICE_DAYS_BEFORE_APPOINTMENT, Math.max(1, d.sendInvoiceDaysBeforeAppointment ?? 3))
                            : d.sendInvoiceDaysBeforeAppointment,
                    }))}
                />
                {(data.sendInvoiceBeforeAppointment ?? false) && (
                    <div style={{ maxWidth: 220, marginTop: 'var(--spacer-S)' }}>
                        <NumberInputField
                            id={`${id}-send-receipt-days-before-appointment`}
                            label={t(TK.sendInvoiceDaysBeforeAppointment)}
                            min={1}
                            max={MAX_SEND_INVOICE_DAYS_BEFORE_APPOINTMENT}
                            value={Math.min(MAX_SEND_INVOICE_DAYS_BEFORE_APPOINTMENT, Math.max(1, data.sendInvoiceDaysBeforeAppointment ?? 3))}
                            onChange={value => setData(d => ({
                                ...d,
                                sendInvoiceDaysBeforeAppointment: Math.min(MAX_SEND_INVOICE_DAYS_BEFORE_APPOINTMENT, Math.max(1, Math.round(value ?? 3))),
                            }))}
                        />
                    </div>
                )}
            </div>

            <div style={{
                marginTop: 'var(--spacer-S)',
                marginBottom: 'var(--spacer-S)',
                padding: 'var(--spacer-S)',
                border: 'var(--border-thin) solid var(--appLigthGrey)',
                borderRadius: 8,
                backgroundColor: 'var(--bg-light)',
            }}>
                <CheckInputField
                    id={`${id}-send-receipt-on-payment`}
                    label={t(TK.sendReceiptOnPayment)}
                    helpText={t(TK.sendReceiptOnPaymentHelp)}
                    value={data.sendReceiptOnPayment ?? false}
                    onChange={value => setData(d => ({
                        ...d,
                        sendReceiptOnPayment: value,
                    }))}
                />
                {(data.sendReceiptOnPayment ?? false) && (
                    <div style={{ marginTop: 'var(--spacer-S)', marginLeft: 'var(--spacer-M)', display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                        <CheckInputField
                            id={`${id}-send-receipt-on-all-payment-methods`}
                            label={t(TK.sendReceiptOnAllPaymentMethods)}
                            value={data.sendReceiptOnAllPaymentMethods ?? true}
                            onChange={value => setData(d => ({
                                ...d,
                                sendReceiptOnAllPaymentMethods: value,
                                ...(value ? { sendReceiptOnPaymentMethods: [], sendReceiptOnOnlinePayments: false } : {}),
                            }))}
                        />
                        {!(data.sendReceiptOnAllPaymentMethods ?? true) && (
                            <>
                                <AutocompleteTextField
                                    id={`${id}-send-receipt-on-payment-methods`}
                                    label={t(TK.sendReceiptOnPaymentMethods)}
                                    helpText={t(TK.sendReceiptOnPaymentMethodsHelp)}
                                    options={paymentMethods}
                                    values={data.sendReceiptOnPaymentMethods ?? []}
                                    onValuesChanged={sendReceiptOnPaymentMethods => setData(d => ({ ...d, sendReceiptOnPaymentMethods }))}
                                    multiSelect
                                />
                                <CheckInputField
                                    id={`${id}-send-receipt-on-online-payments`}
                                    label={t(TK.sendReceiptOnOnlinePayments)}
                                    value={data.sendReceiptOnOnlinePayments ?? false}
                                    onChange={value => setData(d => ({ ...d, sendReceiptOnOnlinePayments: value }))}
                                />
                            </>
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default ServiceConfigurationInvoicesTab;
